import { useState, useCallback, useEffect, useRef } from "react";
import { Card, Col } from "react-bootstrap";
import { Link, useLocation } from "react-router-dom";

import { useDarknetIdentity } from "../../contexts/IdentityContext";
import { useDarknetSocket } from "../../contexts/SocketContext";
import { getProfilePicture } from "../../utilities/Helpers";

import rightArrow from '../../styles/images/right-arrow.png';

const GlobalChatWidget = ({ instanceId = 'global' }: { instanceId?: string }) => {
    const location = useLocation();
    const { identity, authenticated, authenticating } = useDarknetIdentity();
    const { socket } = useDarknetSocket();
    const messagesEndRef = useRef<HTMLLIElement>(null);

    const [showing, setShowing] = useState<boolean>(false);
    const [stats, setStats] = useState<{ connections: number }>({ connections: 0 });
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [message, setMessage] = useState<string>('');

    const handleRecieveMessage = useCallback((chatMessage: ChatMessage) => {
        setMessages(messages => [...messages, chatMessage]);
    }, []);

    useEffect(() => {
        if (socket.connected && !authenticating) {
            if (authenticated) {
                socket.emit('join', instanceId, identity.token ?? undefined);
            } else {
                socket.emit('join', instanceId);
            }

            return () => {
                socket.emit('leave', instanceId);
            }
        }
    }, [authenticated, authenticating, socket.connected, identity.token, instanceId]);

    useEffect(() => {
        socket.on('chat', handleRecieveMessage);

        setInterval(() => {
            socket.emit('stats', { instanceId }, (response) => {
                if (response?.success) {
                    setStats({
                        connections: response.data.connections
                    })
                }
            });
        }, 1000);

        return () => {
            socket.off('chat', handleRecieveMessage);
        }
    }, [handleRecieveMessage, instanceId, socket])

    const handleUpdateMessage = (event: any) => {
        let message = event.target.value;
        if (message.length > 140) return;
        setMessage(event.target.value)
    }

    const handleSendMessage = () => {
        if (message) {
            if (!authenticated) return;
            if (message.length > 140) return;
            socket.emit('chat', { instanceId, message })
            setMessage('');
        }
    }

    const handleShowChat = () => {
        setShowing(!showing);
    }

    const scrollToBottom = () => {
        if (messagesEndRef.current)
            messagesEndRef.current.scrollIntoView({
                behavior: "smooth",
                block: "nearest",
                inline: "start"
            });
    };

    useEffect(() => {
        scrollToBottom()
    }, [messages]);

    let globalChatClass = (showing) ? 'showing' : 'd-block';
    let chatWidgetClass = (showing) ? 'card-widget b-0' : 'd-none';

    if (location.pathname.startsWith('/pong')) {
        globalChatClass = 'd-none';
        chatWidgetClass = 'd-none';
    }

    return (
        <div className={`global-chat ${globalChatClass}`}>
            <button className="dg-btn dg-btn-link px-3 py-2 text-center w-100" onClick={handleShowChat}>{!showing ? "Open" : "Close"} Global Chat</button>
            <Card className={chatWidgetClass} id="chat-widget">
                <Card.Body>
                    <ul id="chat-messages">
                        {messages && messages.map((chatMessage) => {
                            if (chatMessage.sender) {
                                return <li key={chatMessage.id} className="mb-1">
                                    <Link to={`/profile/${chatMessage.sender.address}`} target="_blank" className="clean">
                                        <img src={getProfilePicture(chatMessage.sender.address)} alt="pfp" height={25} />&nbsp;
                                        {chatMessage.sender.name}
                                    </Link>&nbsp;
                                    <span className="fs-8 text-end">{new Date(chatMessage.dateSent).toLocaleTimeString()}</span>
                                    <br />
                                    <span className="fs-7 text-light">{chatMessage.message}</span>
                                    <hr className="m-2" />
                                </li>
                            } else {
                                return <li key={chatMessage.id} className="mb-1">
                                    {chatMessage.message}
                                    <hr className="m-2" />
                                </li>
                            }
                        })}
                        <li ref={messagesEndRef} key="end-message" />
                    </ul>
                </Card.Body>
                <Card.Footer>
                    <div className="input-group">
                        <input type="text" className="dg-textbox p-2" disabled={!authenticated} placeholder="Send message..."
                            value={message}
                            onChange={handleUpdateMessage}
                            onKeyUp={(event: any) => {
                                if (event.key === 'Enter') {
                                    handleSendMessage();
                                }
                            }} />
                        <div className="input-group-append">
                            <button className="dg-btn dg-btn-dark p-2" disabled={!authenticated} onClick={handleSendMessage}>
                                <img src={rightArrow} alt="Send" />
                            </button>
                        </div>
                    </div>
                    <Col className="text-success fs-7 text-end">{stats.connections ?? 0} {"Total Online"}</Col>
                </Card.Footer>
            </Card>
        </div>
    );
}



export default GlobalChatWidget;